import { useState, useCallback } from 'preact/hooks'
import { LogOut, Loader2 } from 'lucide-preact'
import { DropboxIcon } from '@/components/common/icons/DropboxIcon'

export interface DropboxFile {
    fileId: string
    fileName: string
    mimeType: string
    /** Chooser direct content URL (linkType: 'direct'); sent to the API as DirectDownloadUrl. */
    directDownloadUrl: string
    downloadLink: string
    bytes: number
}

export interface DropboxAuthState {
    isConnected: boolean
    providerEmail: string
    isLoading: boolean
    isConnecting: boolean
    connect: () => Promise<void>
    disconnect: () => Promise<void>
}

interface DropboxChooserProps {
    auth: DropboxAuthState
    onFilePicked: (file: DropboxFile) => void
    onError?: (error: string) => void
}

const EXTENSION_MIME_MAP: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    zip: 'application/zip',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    xml: 'application/xml',
    html: 'text/html',
}

function mimeTypeFromExtension(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || ''
    return EXTENSION_MIME_MAP[ext] || 'application/octet-stream'
}

export function DropboxChooser({ auth, onFilePicked, onError }: DropboxChooserProps) {
    const { isConnected, providerEmail, isLoading, isConnecting, connect, disconnect } = auth
    const [pickedFile, setPickedFile] = useState<DropboxFile | null>(null)

    const openChooser = useCallback(() => {
        if (!window.Dropbox) {
            onError?.('Dropbox SDK not loaded. Please refresh the page and try again.')
            return
        }

        window.Dropbox.choose({
            success: (files) => {
                if (files.length > 0) {
                    const chosen = files[0]
                    const file: DropboxFile = {
                        fileId: chosen.id,
                        fileName: chosen.name,
                        mimeType: mimeTypeFromExtension(chosen.name),
                        directDownloadUrl: chosen.link,
                        downloadLink: chosen.link,
                        bytes: chosen.bytes,
                    }
                    setPickedFile(file)
                    onFilePicked(file)
                }
            },
            cancel: () => { /* no-op */ },
            linkType: 'direct',
            multiselect: false,
            extensions: [],
        })
    }, [onFilePicked, onError])

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking Dropbox connection...
            </div>
        )
    }

    if (!isConnected) {
        return (
            <div className="flex justify-center py-6">
                <button
                    type="button"
                    onClick={connect}
                    disabled={isConnecting}
                    className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50"
                >
                    {isConnecting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Connecting...
                        </>
                    ) : (
                        <>
                            <DropboxIcon className="w-5 h-5" />
                            Sign in with Dropbox
                        </>
                    )}
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {/* Connected status */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                    Connected as <span className="font-medium text-slate-700 dark:text-slate-200">{providerEmail}</span>
                </span>
                <button
                    type="button"
                    onClick={disconnect}
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                    <LogOut className="w-3 h-3" />
                    Disconnect
                </button>
            </div>

            {/* Pick file button or picked file display */}
            {pickedFile ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20">
                    <DropboxIcon className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                            {pickedFile.fileName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {pickedFile.mimeType}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setPickedFile(null)
                            openChooser()
                        }}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0"
                    >
                        Change
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={openChooser}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 bg-slate-50 dark:bg-slate-700/30 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                    <DropboxIcon className="w-5 h-5" />
                    Pick from Dropbox
                </button>
            )}
        </div>
    )
}
